import React, { useEffect, useState } from "react";
import {
  Box,
  Modal,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Checkbox,
  List,
  ListItem,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import { useParams } from "react-router-dom";

import type { RootState } from "../../../store/state/rootReducer";
import type { AppDispatch } from "../../../store/state/rootStore";
import { getProjectFolders } from "../../../store/actions/folders";
import useMixPanel from "../../../lib/mixpanel";
import { getCommonFolders } from "../../../utils/getCommonFolders";

interface AddToFoldersModalProps {
  showModal: boolean;
  onCancel: () => void;
  storyId?: string;
  onConfirm: (selectedFolderIds: string[]) => Promise<void>;
}

export const AddToFoldersModal = ({ showModal, onCancel, storyId, onConfirm }: AddToFoldersModalProps) => {
  /**
   * hooks
   */
  const { project_id: projectId, folder_id: folderId } = useParams();

  /**
   * Redux selectors
   */
  const dispatch = useDispatch<AppDispatch>();
  const foldersProject = useSelector((state: RootState) => state.folders?.projectFolders);
  const folderStories = useSelector((state: RootState) => state.folders.folder.stories);
  const selectedStories = useSelector((state: RootState) => state.stories.selectedStories);

  /**
   * state
   */
  const [searchTerm, setSearchTerm] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * variables
   */
  const isFolder = !!(folderId && projectId);
  const selectedStoriesIds = selectedStories.length ? selectedStories.map((s) => s._id) : storyId ? [storyId] : [];
  const currentStoryInFolder = folderStories.find((story) => story._id === storyId);
  const storyFolderIds = currentStoryInFolder?.folders?.map((folder) => folder._id) ?? [];

  const [selectedFolderIds, setSelectedFolderIds] = useState(
    selectedStoriesIds.length ? getCommonFolders(selectedStoriesIds, folderStories, foldersProject) : storyFolderIds
  );

  const filteredFolders = foldersProject.filter((folder) => {
    if (!folder.name) return false;
    if (!searchTerm) return true;
    return folder.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  /**
   * helpers
   */
  const handleCheckboxChange = (checkedFolderId: string) => {
    if (selectedFolderIds.includes(checkedFolderId)) {
      setSelectedFolderIds(selectedFolderIds.filter((val) => val !== checkedFolderId));
    } else {
      setSelectedFolderIds([...selectedFolderIds, checkedFolderId]);
    }
  };

  const addToFoldersHandler = async () => {
    setIsUpdating(true);
    useMixPanel("add_to_folder", { story_id: storyId, new_folders: selectedFolderIds });
    await onConfirm(selectedFolderIds);
    setIsUpdating(false);
  };

  /**
   * effects
   */
  useEffect(() => {
    if (!foldersProject.length && projectId) {
      dispatch(getProjectFolders(projectId));
    }
  }, [foldersProject, projectId, dispatch]);

  /**
   * render
   */
  return (
    <Modal
      open={showModal}
      onClose={onCancel}
      sx={{
        "&:focus": {
          outline: "none",
        },
      }}
    >
      <Box
        sx={{
          background: "white",
          margin: "5% auto",
          padding: "24px",
          maxWidth: "600px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          marginBottom={"24px"}
          lineHeight="24px"
          fontWeight="bold"
          fontSize="18px"
          justifySelf={"flex-start"}
          sx={{ wordWrap: "break-word" }}
          color="#464C53"
        >
          Manage folders
        </Typography>
        <TextField
          autoFocus
          size="small"
          variant="outlined"
          value={searchTerm}
          label="Search folder"
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: "50%",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment
                position="end"
                style={{ opacity: !searchTerm && 0, cursor: "pointer" }}
                onClick={() => setSearchTerm("")}
              >
                <ClearIcon />
              </InputAdornment>
            ),
          }}
        />

        <List
          sx={{
            border: "1px solid #C2C9D1",
            marginTop: "16px",
            marginBottom: "16px",
            borderRadius: 2,
            paddingBottom: 0,
            paddingTop: 0,
            maxHeight: "350px",
            overflow: "auto",
          }}
        >
          {filteredFolders.map((folder, index) => (
            <React.Fragment key={folder._id}>
              <ListItem
                disablePadding
                sx={{
                  height: "50px",
                  cursor: "pointer",
                }}
                onClick={() => handleCheckboxChange(folder._id)}
              >
                <Checkbox checked={selectedFolderIds.includes(folder._id)} />
                <Typography fontWeight="600" fontSize="14px" sx={{ wordWrap: "break-word" }} color="#606971">
                  {folder.name}
                </Typography>
              </ListItem>
              {index !== filteredFolders.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
        <Box display="flex" justifySelf={"flex-end"} justifyContent={"flex-end"} width={"100%"} columnGap="10px">
          <Button
            sx={{
              textTransform: "none",
              "&:focus": {
                outline: "none",
              },
            }}
            variant="outlined"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            sx={{
              width: "100px",
              textTransform: "none",
              "&:focus": {
                outline: "none",
              },
            }}
            variant="contained"
            onClick={addToFoldersHandler}
          >
            {isUpdating ? <CircularProgress size="20px" color="inherit" /> : isFolder ? <>Update</> : <>Add</>}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
